import { memo, ReactElement, useEffect, useMemo, useRef, useState } from 'react'

import { InfoCircleOutlined } from '@ant-design/icons'
import { DragDropContext, DropResult } from '@hello-pangea/dnd'
import { Button, Input, List, Select, Switch, Tooltip } from 'antd'
import { type CheckboxChangeEvent } from 'antd/es/checkbox'
import classNames from 'classnames'
import { produce } from 'immer'
import { type WritableDraft } from 'immer/dist/types/types-external'
import isEqual from 'lodash/isEqual'
import isUndefined from 'lodash/isUndefined'
import sortBy from 'lodash/sortBy'
import { useTranslation } from 'react-i18next'

import { Flex, Icon, Modal } from 'components/core'
import { DraggableListItem, DroppableList } from 'components/DragAndDropList'
import EmptyComponent from 'components/Empty/Empty'
import { ToolTip } from 'components/ToolTip'
import { UtcDatePicker } from 'components/UtcDatePicker/UtcDatePicker'
import { date } from 'configuration/dayjs'
import { TooltipComponentType } from 'configuration/fieldCreators/localTypes'
import { DAY_FORMAT } from 'const/formatting'
import { FieldTooltipProps } from 'features/MetricsGlossary/components/FieldTooltip'
import useMetricCategories from 'features/MetricsGlossary/hooks/useMetricCategories'
import {
    useDataSource,
    useGetDataSourceGroupBysColumns,
    useGetDataSourceMetricsColumns,
} from 'features/MetricsGlossary/queries'
import { reorderArray } from 'helpers/arrays'
import { elementToString } from 'helpers/utilities'
import { Field, PeriodDeltaType } from 'types'

import Setting from './Setting/Setting'
import * as styles from './styles.scss'

interface SelectableField<T>
    extends Omit<Field<T>, 'isVisible' | 'customToolTip'> {
    isVisible: boolean
    customToolTip: TooltipComponentType | undefined
}

function nonFixedColumns<T>(col: Field<T>): boolean {
    return !col.antTableColumnOptions.fixed
}

const OverTheLimitTooltip = ({ children }: FieldTooltipProps): ReactElement => {
    const { t } = useTranslation('table')
    return (
        <ToolTip
            title={t(
                'table:TableSettingsModal.selectionLimit.tooltip',
                'Uncheck an option under “Showing” to add another.'
            )}
        >
            {children}
        </ToolTip>
    )
}

interface Props<T> {
    droppableId: string
    visible: boolean
    onOk: (
        columns: Field<T>[],
        periodDeltaType: PeriodDeltaType,
        periodDeltaDateRange: string[],
        showPeriodDeltas?: boolean,
        showTotalRow?: boolean
    ) => void
    onCancel: () => void
    columns: Field<T>[]
    showPeriodDeltas?: boolean
    periodDeltaType?: PeriodDeltaType
    periodDeltaDateRange?: string[]
    disablePriorPeriod?: 'fade' | 'hide'
    disabledPriorPeriodMessage?: string
    allowCustomPriorPeriod?: boolean
    showTotalRow?: boolean
    displayStateDisabled?: (column: Field<T>) => boolean
    disabledTooltip?: (
        column: Field<T>
    ) => TooltipComponentType<FieldTooltipProps> | undefined
    modalTitle?: string
    customHeader?: ReactElement
    selectionLimit?: number
    placeholder?: string
    dataSourceKey?: string
    metricCategoryOrder?: string[]
}

function TableSettingsModal<T>({
    droppableId,
    visible,
    onOk,
    onCancel,
    columns,
    showPeriodDeltas,
    periodDeltaType = 'prior_period',
    periodDeltaDateRange = [],
    disablePriorPeriod,
    disabledPriorPeriodMessage,
    allowCustomPriorPeriod = true,
    showTotalRow,
    displayStateDisabled = () => false,
    disabledTooltip = () => undefined,
    modalTitle,
    customHeader,
    selectionLimit = Number.MAX_VALUE,
    placeholder,
    dataSourceKey = '',
    metricCategoryOrder,
}: Props<T>): ReactElement {
    const dataSource = useDataSource(dataSourceKey)
    const { data: metricsColumnsData } = useGetDataSourceMetricsColumns(
        dataSource?.slug
    )
    const { data: groupByColumnsData } = useGetDataSourceGroupBysColumns(
        dataSource?.slug
    )

    // no glossary data, so use the columns as is
    const metricsColumns = metricsColumnsData ?? columns
    const groupByColumns = groupByColumnsData ?? columns

    const selectableColumns = useMemo(
        () =>
            columns.filter(nonFixedColumns).map((column) => {
                const metricConfig =
                    metricsColumns.find((metric) => metric.id === column.id) ??
                    ({} as any)
                const groupByConfig =
                    groupByColumns.find(
                        (groupBy) => groupBy.id === column.id
                    ) ?? ({} as any)

                let metricOptions
                if (column.metricOptions || metricConfig?.metricOptions) {
                    metricOptions = {
                        ...column.metricOptions,
                        ...metricConfig?.metricOptions,
                    }
                    // keep the category from the column if it exists
                    if (column.metricOptions?.category) {
                        metricOptions.category = column.metricOptions.category
                    }
                }

                // use the glossary data as much as possible
                // we keep the is visible as it means whether it's selected or not in this context.
                // we keep the custom tooltip as that's a ui concern that isn't in the glossary
                return {
                    ...column,
                    ...metricConfig,
                    ...groupByConfig,
                    isVisible: column.isVisible,
                    customToolTip: column.customToolTip,
                    metricOptions,
                }
            }),
        [columns, metricsColumns, groupByColumns]
    )

    const initialState = {
        filterValue: '',
        showPeriodDeltas: showPeriodDeltas,
        periodDeltaType: periodDeltaType,
        periodDeltaDateRange: periodDeltaDateRange,
        showTotalRow: showTotalRow,
        selectedColumns: selectableColumns.filter(
            (element) => element.isVisible
        ),
        unselectedColumns: sortBy(
            selectableColumns.filter((element) => !element.isVisible),
            ['name']
        ),
    }

    const { t } = useTranslation(['common', 'table'])

    const [filterValue, setFilterValue] = useState(initialState.filterValue)
    const [dirtyShowPeriodDeltas, setDirtyShowPeriodDeltas] = useState(
        initialState.showPeriodDeltas
    )
    const [dirtyPeriodDeltaType, setDirtyPeriodDeltaType] = useState(
        initialState.periodDeltaType
    )
    const [dirtyPeriodDeltaDateRange, setDirtyPeriodDeltaDateRange] = useState(
        initialState.periodDeltaDateRange
    )
    const [dirtyShowTotalRow, setDirtyShowTotalRow] = useState(
        initialState.showTotalRow
    )
    const [selectedColumns, setSelectedColumns] = useState(
        initialState.selectedColumns
    )
    const [unselectedColumns, setUnselectedColumns] = useState(
        initialState.unselectedColumns
    )

    useEffect(() => {
        setSelectedColumns(
            selectableColumns.filter((element) => element.isVisible)
        )
        setUnselectedColumns(
            sortBy(
                selectableColumns.filter((element) => !element.isVisible),
                ['name']
            )
        )
    }, [selectableColumns])

    const doesAnySelectedColumnSupportsPriorPeriod = useMemo(() => {
        const anySelectedColumnSupportsPriorPeriod = selectedColumns.some(
            (column) => !!column.childrenFields?.length
        )
        if (!anySelectedColumnSupportsPriorPeriod) {
            setDirtyShowPeriodDeltas(false)
        }
        return anySelectedColumnSupportsPriorPeriod
    }, [selectedColumns])

    const filteredSelectedColumns = selectedColumns.filter((col) => {
        const f = filterValue.toUpperCase()
        if (!col.name) {
            return false
        }
        const columnName = elementToString(col.name)
        return columnName.toUpperCase().indexOf(f) > -1
    })

    const filteredUnselectedColumns = unselectedColumns.filter((col) => {
        const f = filterValue.toUpperCase()
        if (!col.name) {
            return false
        }
        const columnName = elementToString(col.name)
        return columnName.toUpperCase().indexOf(f) > -1
    })

    const mergeAllColumns = (): Field<T>[] => {
        const leftFixedColumns = columns.filter(
            (col) =>
                col.antTableColumnOptions.fixed === true ||
                col.antTableColumnOptions.fixed === 'left'
        )
        const rightFixedColumns = columns.filter(
            (col) => col.antTableColumnOptions.fixed === 'right'
        )
        return [
            ...leftFixedColumns,
            ...selectedColumns,
            ...unselectedColumns,
            ...rightFixedColumns,
        ]
    }

    const handleUnselectColumn = (
        event: CheckboxChangeEvent,
        column: Field<T>
    ): void => {
        // update-visibility
        const nextColumn = produce(column, (draft) => {
            draft.isVisible = event.target.checked
        })
        // remove from array with selected columns
        setSelectedColumns(
            produce((draft) => {
                const index = draft.findIndex((col) => col.id === nextColumn.id)
                draft.splice(index, 1)
            })
        )
        // add to front of array of unselected columns
        setUnselectedColumns(
            produce((draft) => {
                draft.unshift(nextColumn as WritableDraft<SelectableField<T>>)
            })
        )
    }

    const handleSelectColumn = (
        event: CheckboxChangeEvent,
        column: Field<T>
    ): void => {
        // update visibility
        const nextColumn = produce(column, (draft) => {
            draft.isVisible = event.target.checked
        })
        // add to back of array with selected columns
        setSelectedColumns(
            produce((draft) => {
                draft.push(nextColumn as WritableDraft<SelectableField<T>>)
            })
        )
        // remove-from-array-with-unselected-columns
        setUnselectedColumns(
            produce((draft) => {
                const index = draft.findIndex((col) => col.id === nextColumn.id)
                draft.splice(index, 1)
            })
        )
    }

    const toggleChangeColumns = (checked: boolean): void => {
        setDirtyShowPeriodDeltas(checked)
    }

    const handleDragEnd = (result: DropResult): void => {
        // dropped outside the list
        if (!result.destination) {
            return
        }

        setSelectedColumns(
            reorderArray(
                selectedColumns,
                result.source.index,
                result.destination.index
            )
        )
    }

    const handleCancel = (): void => {
        setFilterValue(initialState.filterValue)
        setDirtyShowPeriodDeltas(initialState.showPeriodDeltas)
        setDirtyPeriodDeltaType(initialState.periodDeltaType)
        setDirtyPeriodDeltaDateRange(initialState.periodDeltaDateRange)
        setDirtyShowTotalRow(initialState.showTotalRow)
        setSelectedColumns(initialState.selectedColumns)
        setUnselectedColumns(initialState.unselectedColumns)

        onCancel()
    }

    const metricCategories = useMetricCategories(
        selectableColumns,
        metricCategoryOrder
    )

    const allCategories = useMemo(() => {
        const categories = metricCategories ?? []
        // if there is a category order, we can assume metricCategories is already sorted
        const innerSortedCategories = metricCategoryOrder?.length
            ? categories
            : sortBy(categories)
        const truthyCategories = innerSortedCategories.filter(Boolean)
        const allNeededCategories = [
            // adding undefined category for attributes columns always at the start.
            undefined,
            ...truthyCategories,
        ]
        return allNeededCategories
    }, [metricCategories, metricCategoryOrder])

    const hasData = allCategories.some((category) => {
        const groupData = filteredUnselectedColumns.filter((item) => {
            if (!item.metricOptions) {
                return category === undefined
            }
            return item.metricOptions?.category === category
        })
        return groupData.length > 0
    })

    const observerRef = useRef<IntersectionObserver | null>(null)
    const headerRefs = useRef<Record<string, HTMLDivElement | null>>({})
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!scrollContainerRef.current) {
            return
        }

        observerRef.current = new IntersectionObserver(() => {}, {
            root: scrollContainerRef.current,
            rootMargin: '0px',
            threshold: [0, 1],
        })

        Object.values(headerRefs.current).forEach((header) => {
            if (header) {
                observerRef.current?.observe(header)
            }
        })

        return () => {
            observerRef.current?.disconnect()
        }
    }, [filteredUnselectedColumns])

    const showTogglesSection =
        (disablePriorPeriod !== 'hide' && !isUndefined(showTotalRow)) ||
        (dirtyShowPeriodDeltas && allowCustomPriorPeriod && !disablePriorPeriod)

    const isAtSelectionLimit = filteredSelectedColumns.length >= selectionLimit

    return (
        <Modal
            style={{
                top: 50,
            }}
            styles={{
                body: {
                    maxHeight: 'calc(100vh - 220px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: 0,
                },
            }}
            wrapClassName={styles['modal-wrap']}
            title={
                modalTitle ??
                t('table:TableSettingsModal.title', 'Customize Table Columns')
            }
            open={visible}
            onCancel={handleCancel}
            width={600}
            footer={[
                <Button onClick={() => handleCancel()} key={1}>
                    {t('common:cancel', 'Cancel')}
                </Button>,

                <Button
                    type="primary"
                    onClick={() =>
                        onOk(
                            mergeAllColumns(),
                            dirtyPeriodDeltaType,
                            dirtyPeriodDeltaDateRange,
                            dirtyShowPeriodDeltas,
                            dirtyShowTotalRow
                        )
                    }
                    key={2}
                >
                    {t('common:save', 'Save')}
                </Button>,
            ]}
        >
            <div
                ref={scrollContainerRef}
                style={{
                    maxHeight: 'calc(100vh - 220px)',
                    overflowY: 'auto',
                    position: 'relative',
                }}
            >
                {customHeader}
                {showTogglesSection && (
                    <div className={styles['change-columns']}>
                        <Flex alignItems="normal">
                            <Flex>
                                <Switch
                                    checked={dirtyShowPeriodDeltas}
                                    onChange={toggleChangeColumns}
                                    disabled={
                                        disablePriorPeriod === 'fade' ||
                                        !doesAnySelectedColumnSupportsPriorPeriod
                                    }
                                />
                                <span
                                    className={classNames({
                                        [styles['text-disabled']]:
                                            disablePriorPeriod === 'fade',
                                    })}
                                    style={{
                                        marginLeft: 8,
                                        marginRight: 8,
                                    }}
                                >
                                    {t(
                                        'common:compareWithPriorPeriod',
                                        'Compare metrics with prior period'
                                    )}
                                </span>
                                <Icon
                                    name="ARROW_RIGHT"
                                    rotate={-90}
                                    color="colorDeltaGreen"
                                />
                                <Icon
                                    name="ARROW_RIGHT"
                                    rotate={90}
                                    color="colorDeltaRed"
                                />
                                {disablePriorPeriod === 'fade' && (
                                    <Tooltip title={disabledPriorPeriodMessage}>
                                        <InfoCircleOutlined
                                            style={{ marginLeft: 8 }}
                                        />
                                    </Tooltip>
                                )}
                            </Flex>
                            {!isUndefined(showTotalRow) && (
                                <Flex marginLeft={20}>
                                    <Switch
                                        checked={dirtyShowTotalRow}
                                        onChange={() =>
                                            setDirtyShowTotalRow((b) => !b)
                                        }
                                    />
                                    <span
                                        style={{
                                            marginLeft: 8,
                                            marginRight: 8,
                                        }}
                                    >
                                        {t(
                                            'table:TableSettingsModal.TotalRow.title',
                                            'Show metric total row'
                                        )}
                                    </span>
                                </Flex>
                            )}
                        </Flex>
                        {dirtyShowPeriodDeltas &&
                            allowCustomPriorPeriod &&
                            !disablePriorPeriod && (
                                <div className={styles['period-delta-config']}>
                                    <div
                                        className={
                                            styles['period-delta-config-type']
                                        }
                                    >
                                        <span>
                                            {t(
                                                'common:compareTo',
                                                'Compare to:'
                                            )}
                                        </span>
                                        <Select
                                            style={{
                                                marginLeft: '10px',
                                                flex: 1,
                                            }}
                                            value={dirtyPeriodDeltaType}
                                            onChange={(value) =>
                                                setDirtyPeriodDeltaType(value)
                                            }
                                        >
                                            <Select.Option value="prior_period">
                                                {t(
                                                    'table:TableSettingsModal.PeriodDeltas.options.prior_period',
                                                    'Prior Period'
                                                )}
                                            </Select.Option>
                                            <Select.Option value="prior_year">
                                                {t(
                                                    'table:TableSettingsModal.PeriodDeltas.options.prior_year',
                                                    'Year-Over-Year'
                                                )}
                                            </Select.Option>
                                            <Select.Option value="custom">
                                                {t(
                                                    'table:TableSettingsModal.PeriodDeltas.options.custom',
                                                    'Custom'
                                                )}
                                            </Select.Option>
                                        </Select>
                                    </div>
                                    {dirtyPeriodDeltaType === 'custom' && (
                                        <UtcDatePicker.RangePicker
                                            className={
                                                styles[
                                                    'period-delta-date-picker'
                                                ]
                                            }
                                            value={
                                                dirtyPeriodDeltaDateRange.length ===
                                                2
                                                    ? [
                                                          date(
                                                              dirtyPeriodDeltaDateRange[0]
                                                          ),
                                                          date(
                                                              dirtyPeriodDeltaDateRange[1]
                                                          ),
                                                      ]
                                                    : null
                                            }
                                            onChange={(values) =>
                                                setDirtyPeriodDeltaDateRange(
                                                    values
                                                        ? values.map((day) =>
                                                              day
                                                                  ? day.format(
                                                                        'YYYY-MM-DD'
                                                                    )
                                                                  : ''
                                                          )
                                                        : []
                                                )
                                            }
                                            format={DAY_FORMAT}
                                        />
                                    )}
                                </div>
                            )}
                    </div>
                )}
                <div
                    style={{
                        position: 'sticky',
                        top: 0,
                        marginTop: '1px', // Allows the border in the section above to show
                        zIndex: 3,
                        background: '#fff',
                        padding: '16px 24px 8px',
                    }}
                >
                    <Input
                        placeholder={
                            placeholder ??
                            t(
                                'table:TableSettingsModal.columnSearch.placeholder',
                                'Search for columns...'
                            )
                        }
                        onChange={(e) => {
                            setFilterValue(e.currentTarget.value)
                        }}
                        style={{
                            width: '100%',
                            height: '36px',
                        }}
                        value={filterValue}
                        allowClear
                        prefix={<Icon name="SEARCH" margin="0 5px 0 0" />}
                    />
                </div>

                <div>
                    {filteredSelectedColumns.length > 0 && (
                        <div
                            style={{
                                padding: '8px 24px',
                            }}
                        >
                            <div className={styles['sticky-section-header']}>
                                {t(
                                    'table:TableSettingsModal.shownInTable',
                                    'Showing'
                                )}
                                <span
                                    className={styles['sticky-section-count']}
                                >
                                    {filteredSelectedColumns.length}
                                </span>
                            </div>
                            <div className={styles['column-list']}>
                                <DragDropContext onDragEnd={handleDragEnd}>
                                    <DroppableList droppableId={droppableId}>
                                        {filteredSelectedColumns.map(
                                            (column, index) => (
                                                <DraggableListItem
                                                    key={column.id}
                                                    id={column.id}
                                                    index={index}
                                                >
                                                    <Setting
                                                        column={column}
                                                        displayStateDisabled={
                                                            false
                                                        }
                                                        onCheckboxChange={
                                                            handleUnselectColumn
                                                        }
                                                        dataSource={dataSource}
                                                    />
                                                </DraggableListItem>
                                            )
                                        )}
                                    </DroppableList>
                                </DragDropContext>
                            </div>
                        </div>
                    )}

                    {filteredUnselectedColumns.length > 0 && (
                        <div
                            style={{
                                padding: '8px 24px',
                            }}
                        >
                            <div className={styles['sticky-section-header']}>
                                <div className={styles['sticky-section-title']}>
                                    {t(
                                        'table:TableSettingsModal.hiddenInTable',
                                        'Not Showing'
                                    )}
                                    <span
                                        className={
                                            styles['sticky-section-count']
                                        }
                                    >
                                        {filteredUnselectedColumns.length}
                                    </span>
                                </div>
                                {isAtSelectionLimit && (
                                    <span
                                        className={
                                            styles['sticky-section-limit']
                                        }
                                    >
                                        (
                                        {t(
                                            'table:TableSettingsModal.selectionLimit.indicator',
                                            'selection limit of {{ selectionLimit }} reached',
                                            { selectionLimit }
                                        )}
                                        )
                                    </span>
                                )}
                            </div>
                            <div className={styles['column-list']}>
                                {hasData ? (
                                    allCategories.map((category) => {
                                        const groupData =
                                            filteredUnselectedColumns.filter(
                                                (item) => {
                                                    if (!item.metricOptions) {
                                                        return (
                                                            category ===
                                                            undefined
                                                        )
                                                    }
                                                    return (
                                                        item.metricOptions
                                                            ?.category ===
                                                        category
                                                    )
                                                }
                                            )

                                        if (groupData.length === 0) {
                                            return null
                                        }

                                        return (
                                            <div
                                                className={
                                                    styles['list-section']
                                                }
                                                key={category ?? 'Attributes'}
                                            >
                                                <div
                                                    className={
                                                        styles[
                                                            'list-section-header'
                                                        ]
                                                    }
                                                    ref={(el) => {
                                                        if (el) {
                                                            headerRefs.current[
                                                                category ??
                                                                    'Attributes'
                                                            ] = el
                                                        }
                                                    }}
                                                    data-category={
                                                        category ?? 'undefined'
                                                    }
                                                >
                                                    {category ||
                                                        t(
                                                            'table:TableSettingsModal.uncategorized',
                                                            'Attributes'
                                                        )}
                                                    <span
                                                        className={
                                                            styles[
                                                                'list-section-count'
                                                            ]
                                                        }
                                                    >
                                                        {groupData.length}
                                                    </span>
                                                </div>
                                                <List
                                                    dataSource={groupData}
                                                    renderItem={(item) => (
                                                        <List.Item>
                                                            <Setting<T>
                                                                column={item}
                                                                displayStateDisabled={
                                                                    isAtSelectionLimit ||
                                                                    displayStateDisabled(
                                                                        item
                                                                    )
                                                                }
                                                                disabledTooltip={
                                                                    isAtSelectionLimit
                                                                        ? OverTheLimitTooltip
                                                                        : disabledTooltip(
                                                                              item
                                                                          )
                                                                }
                                                                onCheckboxChange={
                                                                    handleSelectColumn
                                                                }
                                                                isDraggable={
                                                                    false
                                                                }
                                                                dataSource={
                                                                    dataSource
                                                                }
                                                            />
                                                        </List.Item>
                                                    )}
                                                />
                                            </div>
                                        )
                                    })
                                ) : (
                                    <EmptyComponent
                                        description={t(
                                            'table:TableSettingsModal.noData',
                                            'No data'
                                        )}
                                        style={{ marginTop: 20 }}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}

export default memo(TableSettingsModal, isEqual) as typeof TableSettingsModal
